/* Paid trial-class booking: course (pro-rated 1/4 fee) -> instrument-matched
   teacher with availability -> live slot hold -> details -> Stripe checkout.
   Payment return (?session_id=) polls the public status endpoint. */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { useList, errMsg, fmtRM, DAYS, mediaUrl } from "@/lib/api";
import { PublicLayout } from "./PublicSite";
import { Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Music4, CheckCircle2, ArrowLeft, CalendarDays, XCircle, Loader2 } from "lucide-react";

const initials = (n) => (n || "").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const todayStr = () => new Date().toISOString().slice(0, 10);

const STEPS = ["Course", "Date & time", "Details & pay"];
const StepBar = ({ step }) => (
  <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="trial-step-bar">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-2">
        <span className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center
          ${i < step ? "bg-[#2D6A4F] text-white" : i === step ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>
          {i < step ? "✓" : i + 1}
        </span>
        <span className={`text-xs font-medium ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
        {i < STEPS.length - 1 && <span className="w-6 h-px bg-border" />}
      </div>
    ))}
  </div>
);

function HoldCountdown({ expiresAt, onExpired }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const s = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
      setLeft(s);
      if (s <= 0) onExpired();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt, onExpired]);
  return (
    <p className="text-xs font-semibold text-secondary" data-testid="trial-hold-countdown">
      Slot held for {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
    </p>
  );
}

// ---- Stripe return screen: polls until paid / expired ----
function PaymentReturn({ sessionId }) {
  const [state, setState] = useState({ phase: "checking" });
  const tries = useRef(0);
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/public/trial-bookings/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ phase: "paid", b: data }); return; }
        if (data.status === "expired") { setState({ phase: "expired" }); return; }
      } catch { /* keep polling */ }
      if (tries.current >= 10) { setState({ phase: "timeout" }); return; }
      setTimeout(poll, 3000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="trial-payment-return">
        {state.phase === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground">One moment — don't close this page.</p>
          </>
        )}
        {state.phase === "paid" && (
          <div data-testid="trial-success">
            <CheckCircle2 className="h-14 w-14 text-[#2D6A4F] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Trial class confirmed!</h1>
            <p className="text-muted-foreground">Reference <span className="font-mono font-bold text-foreground" data-testid="trial-reference">{state.b.reference}</span></p>
            <div className="rounded-lg border bg-card p-6 mt-8 text-left text-sm space-y-2">
              <p><span className="text-muted-foreground">Student:</span> <b>{state.b.student_name}</b></p>
              <p><span className="text-muted-foreground">Course:</span> <b>{state.b.course_name}</b></p>
              <p><span className="text-muted-foreground">Music Instructor:</span> <b>{state.b.teacher_name}</b></p>
              <p><span className="text-muted-foreground">When:</span> <b>{state.b.selected_date} · {state.b.selected_start_time}–{state.b.selected_end_time}</b></p>
              <p className="pt-2 border-t"><span className="text-muted-foreground">Paid:</span> <b className="font-display">{fmtRM(state.b.trial_fee)}</b></p>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              A confirmation has been sent by email/WhatsApp to you, the Music Instructor and the school — and you&apos;ll get a reminder the day before the class. 🎵
            </p>
            <Button asChild className="mt-8" data-testid="trial-home-button"><Link to="/">Back to home</Link></Button>
          </div>
        )}
        {(state.phase === "expired" || state.phase === "timeout") && (
          <div data-testid="trial-payment-failed">
            <XCircle className="h-14 w-14 text-destructive mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-2xl font-bold mb-3">
              {state.phase === "expired" ? "Payment session expired" : "We couldn't confirm the payment yet"}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              {state.phase === "expired"
                ? "No charge was made. Please book your trial again."
                : "If you completed payment, the confirmation will arrive by email shortly — or contact the school office."}
            </p>
            <Button asChild variant="outline"><Link to="/trial-booking">Book again</Link></Button>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

export default function TrialBookingPage() {
  const search = new URLSearchParams(window.location.search);
  const returnedSession = search.get("session_id");

  const [step, setStep] = useState(0);
  const [session, setSession] = useState(null);
  const { data: courses } = useList(returnedSession ? null : "/public/courses");
  const [courseTeachers, setCourseTeachers] = useState(null);
  const [course, setCourse] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [holidayInfo, setHolidayInfo] = useState(null);
  const [hold, setHold] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [pendingMaster, setPendingMaster] = useState(null);
  const holdRef = useRef(null);
  holdRef.current = hold;

  useEffect(() => {
    if (returnedSession) return;
    api.post("/public/registration/session", { source_channel: "website" })
      .then((r) => setSession(r.data))
      .catch(() => toast.error("Could not start booking — please refresh."));
    return () => {
      if (holdRef.current) api.post(`/public/booking-holds/${holdRef.current.id}/release`).catch(() => {});
    };
  }, [returnedSession]);

  if (returnedSession) return <PaymentReturn sessionId={returnedSession} />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const releaseHold = () => {
    if (hold) {
      api.post(`/public/booking-holds/${hold.id}/release`).catch(() => {});
      setHold(null);
    }
  };

  const chooseCourse = async (c) => {
    setCourse(c); setTeacher(null); setStep(1); setCourseTeachers(null); setPendingMaster(null);
    try {
      const r = await api.get(`/public/course-teachers?course_id=${c.id}`);
      setCourseTeachers(r.data.teachers);
    } catch { setCourseTeachers([]); }
  };
  const onTileClick = (tile) => {
    if (tile.is_master) {
      setPendingMaster(pendingMaster?.master_course_name === tile.master_course_name ? null : tile);
    } else {
      chooseCourse(tile);
    }
  };

  const loadSlots = async (d, t = teacher) => {
    setLoadingSlots(true); setSlots(null); setHolidayInfo(null);
    try {
      const r = t
        ? await api.get(`/public/booking/slots?teacher_id=${t.id}&date=${d}`)
        : await api.get(`/public/booking/slots-by-course?course_id=${course.id}&date=${d}`);
      setSlots(r.data.slots);
      if (r.data.is_holiday) setHolidayInfo({ label: r.data.holiday_label });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoadingSlots(false); }
  };

  const nextDateForDay = (dayIdx) => {
    const d = new Date();
    const jsTarget = (dayIdx + 1) % 7;
    let add = (jsTarget - d.getDay() + 7) % 7;
    if (add === 0) add = 7;
    d.setDate(d.getDate() + add);
    return d.toISOString().slice(0, 10);
  };

  const pickTeacherDay = (t, dayIdx) => {
    setTeacher(t);
    const d = nextDateForDay(dayIdx);
    setDate(d); releaseHold(); setStep(1); loadSlots(d, t);
  };

  const pickSlot = async (slot) => {
    if (!session) { toast.error("Still initialising — one moment please"); return; }
    releaseHold();
    // Course-mode slots come tagged with a teacher_id we should auto-pick.
    const slotTeacherId = teacher?.id || slot.teacher_id;
    if (!teacher && slot.teacher_id) {
      setTeacher({ id: slot.teacher_id, full_name: slot.teacher_name });
    }
    try {
      const r = await api.post("/public/booking-holds", {
        session_id: session.session_id, course_id: course.id, teacher_id: slotTeacherId,
        studio_id: slot.studio_id, selected_date: date,
        start_time: slot.start_time, end_time: slot.end_time,
      });
      setHold(r.data);
      toast.success(`Slot held: ${slot.start_time} on ${date}${slot.teacher_name ? ` with ${slot.teacher_name}` : ""}`);
    } catch (e) { toast.error(errMsg(e)); loadSlots(date); }
  };

  const payNow = async () => {
    if (!form.parent_name || !form.parent_phone || !form.student_name) {
      toast.error("Parent name, phone and student name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/public/trial-bookings", {
        session_id: session.session_id, hold_id: hold.id,
        parent_name: form.parent_name, parent_phone: form.parent_phone,
        parent_email: form.parent_email || null, student_name: form.student_name,
        student_age: form.student_age ? parseInt(form.student_age, 10) : null,
        notes: form.notes || null, origin_url: window.location.origin,
      });
      window.location.href = r.data.url; // Stripe checkout
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14" data-testid="trial-booking-page">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Pro-rated Trial Class</h1>
        <p className="text-muted-foreground mb-10 max-w-xl">
          Try a single lesson at the <b className="text-foreground">Pro-rated Trial Class Fee</b> — pick a Music Course, Music Instructor and time, pay online, done.
        </p>
        <StepBar step={step} />

        {step === 0 && (
          <div data-testid="trial-step-course">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in">
              {(courses || []).map((c) => {
                const isExpanded = c.is_master && pendingMaster?.master_course_name === c.master_course_name;
                return (
                <div key={c.id} className={`rounded-lg border bg-card overflow-hidden transition-colors ${isExpanded ? "border-secondary md:col-span-2 lg:col-span-3" : "lift hover:border-secondary"} ${course?.id === c.id ? "border-secondary" : ""}`}>
                <button disabled={!session} onClick={() => onTileClick(c)}
                  data-testid={`trial-course-${c.id}`}
                  className="text-left w-full disabled:opacity-50 disabled:cursor-wait">
                  {c.image_url
                    ? <img src={mediaUrl(c.image_url)} alt={c.image_alt_text || c.name} loading="lazy" className="w-full h-32 object-cover" />
                    : <div className="w-full h-32 bg-accent flex items-center justify-center"><Music4 className="h-8 w-8 text-secondary" strokeWidth={1.5} /></div>}
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold">{c.name}</p>
                      {c.is_master && <span className="text-[10px] uppercase font-bold tracking-wide text-secondary bg-secondary/10 px-2 py-0.5 rounded">{c.level_count} levels</span>}
                    </div>
                    {(c.age_group || c.lesson_duration) && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {c.age_group && <>Age {c.age_group}</>}{c.age_group && c.lesson_duration && " · "}{c.lesson_duration}
                      </p>
                    )}
                    {c.trial_fee && <p className="text-sm font-bold font-display mt-2">{c.is_master ? "Trial from " : "Trial "}{fmtRM(c.trial_fee)} <span className="text-[10px] font-normal text-muted-foreground">(¼ of {fmtRM(c.fee_from)}/mo)</span></p>}
                    {c.is_master && !isExpanded && <p className="text-xs text-secondary font-semibold mt-2">Tap to pick your level →</p>}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t bg-secondary/5 p-5" data-testid={`trial-master-levels-${c.id}`}>
                    <p className="text-xs text-muted-foreground mb-3">Pick the level for your trial class:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {c.levels.map((lv) => (
                        <button key={lv.id} onClick={() => chooseCourse(lv)}
                          data-testid={`trial-choose-level-${lv.id}`}
                          className="rounded-md border bg-card hover:border-secondary p-3 text-left transition">
                          <p className="text-sm font-semibold">{lv.name.replace(c.master_course_name, "").trim() || `Level ${lv.level_within_master}`}</p>
                          <p className="text-xs text-muted-foreground">{lv.trial_fee ? `Trial ${fmtRM(lv.trial_fee)}` : "—"}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              );})}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="trial-step-slot">
            <p className="text-sm text-muted-foreground mb-5">
              Pick a day &amp; time that works for you — we&apos;ll match you with an available Music Instructor for <b className="text-foreground">{course?.name}</b>{teacher ? <> ({teacher.full_name})</> : null}.
            </p>
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trial date</Label>
                <Input type="date" min={todayStr()} value={date} data-testid="trial-date-input"
                  onChange={(e) => { setDate(e.target.value); releaseHold(); if (e.target.value) loadSlots(e.target.value); }}
                  className="w-48" />
              </div>
              {hold && <HoldCountdown expiresAt={hold.expires_at} onExpired={() => { setHold(null); toast.error("Slot hold expired — please pick a time again"); if (date) loadSlots(date); }} />}
            </div>
            {loadingSlots ? <Spinner /> : slots == null ? (
              <p className="text-sm text-muted-foreground">Choose a date to see {teacher?.full_name}'s free times.</p>
            ) : holidayInfo ? (
              <div className="rounded-lg border-2 border-dashed border-secondary/50 bg-secondary/5 p-6 text-center">
                <CalendarDays className="h-7 w-7 text-secondary mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-semibold text-foreground">{holidayInfo.label}</p>
                <p className="text-xs text-muted-foreground mt-1">School closed on this date — please choose another day.</p>
              </div>
            ) : slots.length === 0 ? (
              <EmptyState text="No free slots on this date — try another day." />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 stagger-in" data-testid="trial-slot-grid">
                {slots.map((s) => {
                  const isHeld = hold && hold.start_time === s.start_time && hold.end_time === s.end_time;
                  const slotTeacher = s.teacher_name || teacher?.full_name;
                  return (
                    <button key={`${s.start_time}_${s.teacher_id || ''}`} onClick={() => pickSlot(s)}
                      data-testid={`trial-slot-${s.start_time.replace(":", "")}`}
                      className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-md border text-left transition-colors
                        ${isHeld ? "bg-secondary text-white border-secondary" : "bg-card hover:border-secondary"}`}>
                      <span className="text-sm font-semibold font-mono">{s.start_time}–{s.end_time}</span>
                      {slotTeacher && (
                        <span className={`text-[10px] truncate w-full ${isHeld ? "text-white/90" : "text-muted-foreground"}`}>
                          with {slotTeacher}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-3 mt-8">
              <Button variant="outline" onClick={() => { releaseHold(); setStep(0); }}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
              <Button disabled={!hold} onClick={() => setStep(2)} data-testid="trial-continue-details">Continue</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-lg space-y-4" data-testid="trial-step-details">
            <div className="rounded-lg border bg-card p-4 text-sm space-y-1">
              <p><span className="text-muted-foreground">Trial:</span> <b>{course?.name}</b> with <b>{teacher?.full_name}</b></p>
              <p><span className="text-muted-foreground">When:</span> <b>{date} · {hold?.start_time}–{hold?.end_time}</b></p>
              <p className="pt-1 border-t"><span className="text-muted-foreground">To pay now:</span> <b className="font-display" data-testid="trial-price">{fmtRM(course?.trial_fee)}</b> <span className="text-xs text-muted-foreground">(¼ of the {fmtRM(course?.fee_from)} monthly fee)</span></p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parent name *</Label>
                <Input data-testid="trial-parent-name" value={form.parent_name || ""} onChange={(e) => set("parent_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone (WhatsApp) *</Label>
                <Input data-testid="trial-parent-phone" value={form.parent_phone || ""} onChange={(e) => set("parent_phone", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
                <Input data-testid="trial-parent-email" type="email" value={form.parent_email || ""} onChange={(e) => set("parent_email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student name *</Label>
                <Input data-testid="trial-student-name" value={form.student_name || ""} onChange={(e) => set("student_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student age</Label>
                <Input data-testid="trial-student-age" type="number" value={form.student_age || ""} onChange={(e) => set("student_age", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</Label>
                <Input data-testid="trial-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
            {hold && <HoldCountdown expiresAt={hold.expires_at} onExpired={() => { setHold(null); toast.error("Slot hold expired — please pick a time again"); setStep(1); if (date) loadSlots(date); }} />}
            <div className="flex items-center gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
              <Button onClick={payNow} disabled={busy || !hold} data-testid="trial-pay-button">
                {busy ? "Redirecting to payment..." : `Pay ${fmtRM(course?.trial_fee)} & confirm`}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Secure payment by Stripe. You, the Music Instructor and the school will receive instant confirmation, plus a reminder the day before the class.
            </p>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
